<script setup lang="ts">
import { computed } from 'vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components';
import { CrChartData } from '@Shared/models/CuratedReports/CrChartData';

use([SVGRenderer, BarChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent]);

interface Props {
  data: CrChartData;
  height?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  height: '100%',
});

const chartData = computed(() => {
  if (props.data?.data?.rollupData?.rowDimension?.values) {
    const rowDimension = props.data.data.rollupData.rowDimension;

    const months = rowDimension.values.map((item) => item.value);
    const detractors = rowDimension.values.map(
      (item) =>
        item.columnDimension?.values.find((cv) => cv.value === 'Detractor')?.totalMeasures.RecordCount?.CurrentValue ||
        0
    );
    const neutrals = rowDimension.values.map(
      (item) =>
        item.columnDimension?.values.find((cv) => cv.value === 'Neutral')?.totalMeasures.RecordCount?.CurrentValue || 0
    );
    const promoters = rowDimension.values.map(
      (item) =>
        item.columnDimension?.values.find((cv) => cv.value === 'Promoter')?.totalMeasures.RecordCount?.CurrentValue || 0
    );

    // NPS is already calculated as percentage in the data
    const nps = rowDimension.values.map((item) => item.totalMeasures.Msr_Svy_Data_NPS_IndexAverage?.CurrentValue || 0);

    const totalResponses = rowDimension.values.map((item) => item.totalMeasures.RecordCount?.CurrentValue || 0);

    return {
      months,
      detractors,
      neutrals,
      promoters,
      nps,
      totalResponses,
    };
  }

  // No data provided - return empty arrays
  return {
    months: [],
    detractors: [],
    neutrals: [],
    promoters: [],
    nps: [],
    totalResponses: [],
  };
});

const chartOptions = computed(() => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow',
    },
    formatter: (params: any) => {
      const dataIndex = params[0].dataIndex;
      const months = chartData.value.months;
      const month = months[dataIndex];
      if (!month) return '';

      const detractorCount = chartData.value.detractors[dataIndex] || 0;
      const neutralCount = chartData.value.neutrals[dataIndex] || 0;
      const promoterCount = chartData.value.promoters[dataIndex] || 0;
      const totalResponses = chartData.value.totalResponses[dataIndex] || 0;
      const npsValue = chartData.value.nps[dataIndex] || 0;

      const categoryTotal = detractorCount + neutralCount + promoterCount;
      const detractorPercent = categoryTotal > 0 ? (detractorCount / categoryTotal) * 100 : 0;
      const neutralPercent = categoryTotal > 0 ? (neutralCount / categoryTotal) * 100 : 0;
      const promoterPercent = categoryTotal > 0 ? (promoterCount / categoryTotal) * 100 : 0;

      let html = `<div style="font-weight: bold; margin-bottom: 8px;">${month}</div>`;
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span>Detractors:</span>
        <span style="color: #dc3545; font-weight: bold;">${detractorPercent.toFixed(0)}%</span>
      </div>`;
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span>Neutrals:</span>
        <span style="color: #7b7b7b; font-weight: bold;">${neutralPercent.toFixed(0)}%</span>
      </div>`;
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span>Promoters:</span>
        <span style="color: #0c8a65; font-weight: bold;">${promoterPercent.toFixed(0)}%</span>
      </div>`;
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 4px; border-top: 1px solid #eee; padding-top: 40px;">
        <span>Total Responses:</span>
        <span style="font-weight: bold;">${totalResponses}</span>
      </div>`;
      html += `<div style="display: flex; justify-content: space-between;">
        <span style="font-size: 14px; font-weight: bold;">NPS:</span>
        <span style="font-size: 14px; font-weight: bold; color: ${npsValue >= 0 ? '#0c8a65' : '#dc3545'};">${npsValue > 0 ? '+' : ''}${Math.round(npsValue * 100)}</span>
      </div>`;

      return html;
    },
  },
  legend: {
    data: ['Detractors', 'Neutrals', 'Promoters', 'NPS'],
    top: 10,
    itemGap: 20,
    textStyle: {
      fontSize: 12,
    },
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    top: '10%',
    containLabel: true,
  },
  xAxis: {
    type: 'category',
    data: chartData.value.months,
    axisLabel: {
      fontSize: 11,
      color: '#666',
      interval: 0,
      formatter: (value: string, index: number) => {
        const total = chartData.value.totalResponses[index] || 0;
        return `{a|${value}}\n\n{b|${total}}\n{c|Responses}`;
      },
      lineHeight: 16,
      rich: {
        a: {
          fontSize: 10,
          fontWeight: 'normal',
        },
        b: {
          fontSize: 14,
          fontWeight: 'bold',
        },
        c: {
          fontSize: 10,
        },
      },
    },
    axisLine: {
      lineStyle: {
        color: '#ddd',
      },
    },
    axisTick: {
      show: false,
    },
  },
  yAxis: [
    {
      type: 'value',
      name: 'Proportion of Respondents',
      min: 0,
      max: 100,
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 40,
      nameTextStyle: {
        fontSize: 11,
        color: '#666',
      },
      axisLabel: {
        formatter: '{value}%',
        fontSize: 11,
        color: '#666',
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
        },
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0',
          type: 'dashed',
        },
      },
    },
    {
      type: 'value',
      name: 'NPS',
      nameTextStyle: {
        color: 'transparent',
      },
      axisLabel: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: false,
      },
    },
  ],
  series: [
    {
      name: 'Detractors',
      animationDuration: 500,
      animationEasing: 'cubicIn',
      animationDelay: function (dataIndex: number) {
        return dataIndex * 50; // 50ms delay between each item
      },
      type: 'bar',
      stack: 'responses',
      data: chartData.value.detractors.map((d, i) => {
        const denom =
          (chartData.value.detractors[i] || 0) +
          (chartData.value.neutrals[i] || 0) +
          (chartData.value.promoters[i] || 0);
        return denom > 0 ? (d / denom) * 100 : 0;
      }),
      itemStyle: {
        color: '#dc3545',
      },
      barWidth: '60%',
      label: {
        show: true,
        position: 'inside',
        formatter: (params: any) => `${Math.round(params.value)}%`,
        fontSize: 10,
        color: '#fff',
        labelLayout: {
          hideOverlap: false,
        },
      },
    },
    {
      name: 'Neutrals',
      animationDuration: 300,
      animationEasing: 'linear',
      animationDelay: function (dataIndex: number) {
        return 500 + dataIndex * 50; // 50ms delay between each item
      },
      type: 'bar',
      stack: 'responses',
      data: chartData.value.neutrals.map((d, i) => {
        const denom =
          (chartData.value.detractors[i] || 0) +
          (chartData.value.neutrals[i] || 0) +
          (chartData.value.promoters[i] || 0);
        return denom > 0 ? (d / denom) * 100 : 0;
      }),
      itemStyle: {
        color: '#7b7b7b',
      },
      barWidth: '60%',
      label: {
        show: true,
        position: 'inside',
        formatter: (params: any) => `${Math.round(params.value)}%`,
        fontSize: 10,
        color: '#fff',
        labelLayout: {
          hideOverlap: false,
        },
      },
    },
    {
      name: 'Promoters',
      animationDuration: 600,
      animationEasing: 'cubicOut',
      animationDelay: function (dataIndex: number) {
        return 800 + dataIndex * 50; // 50ms delay between each item
      },
      type: 'bar',
      stack: 'responses',
      data: chartData.value.promoters.map((d, i) => {
        const denom =
          (chartData.value.detractors[i] || 0) +
          (chartData.value.neutrals[i] || 0) +
          (chartData.value.promoters[i] || 0);
        return denom > 0 ? (d / denom) * 100 : 0;
      }),
      itemStyle: {
        color: '#0c8a65',
      },
      barWidth: '60%',
      label: {
        show: true,
        position: 'inside',
        formatter: (params: any) => `${Math.round(params.value)}%`,
        fontSize: 10,
        color: '#fff',
        labelLayout: {
          hideOverlap: false,
        },
      },
    },
    {
      name: 'NPS',
      type: 'line',
      yAxisIndex: 1,
      data: chartData.value.nps,
      itemStyle: {
        color: '#6f42c1',
      },
      lineStyle: {
        width: 3,
        color: '#6f42c1',
      },
      symbol: 'circle',
      symbolSize: 8,
      showSymbol: true,
      smooth: false,
      label: {
        show: true,
        position: 'top',
        formatter: (params: any) => {
          const npsPercent = Math.round(params.value * 100);
          return `${npsPercent > 0 ? '+' : ''}${npsPercent}`;
        },
        fontSize: 10,
        color: '#6f42c1',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 4,
        padding: [2, 4],
        labelLayout: {
          hideOverlap: false,
        },
      },
    },
  ],
}));
</script>

<template>
  <div class="nps-stacked-bar-chart" :style="{ height: typeof height === 'string' ? height : height }">
    <VChart :option="chartOptions" :autoresize="true" class="chart-container" />
  </div>
</template>

<style scoped lang="css">
.nps-stacked-bar-chart {
  width: 100%;
  position: relative;
}

.chart-container {
  width: 100%;
  height: 100%;
}
</style>
